import { lazy, Suspense } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/features/auth/useAuth";
import { PageLoader } from "@/components/ui/LoadingState";
import { AppErrorBoundary } from "@/components/ui/AppErrorBoundary";

const AppShell = lazy(() => import("@/components/layout/AppShell").then((module) => ({ default: module.AppShell })));
const LoginPage = lazy(() => import("@/pages/LoginPage").then((module) => ({ default: module.LoginPage })));
const PortfolioPage = lazy(() => import("@/pages/PortfolioPage").then((module) => ({ default: module.PortfolioPage })));
const CitiesPage = lazy(() => import("@/pages/CitiesPage").then((module) => ({ default: module.CitiesPage })));
const CityPage = lazy(() => import("@/pages/CityPage").then((module) => ({ default: module.CityPage })));
const RoiPage = lazy(() => import("@/pages/RoiPage").then((module) => ({ default: module.RoiPage })));
const ScenariosPage = lazy(() => import("@/pages/ScenariosPage").then((module) => ({ default: module.ScenariosPage })));
const ReportsPage = lazy(() => import("@/pages/ReportsPage").then((module) => ({ default: module.ReportsPage })));
const DataAdminPage = lazy(() => import("@/pages/DataAdminPage").then((module) => ({ default: module.DataAdminPage })));

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth();
  if (isLoading) return <PageLoader />;
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  return <>{children}</>;
}

function AppRoutes() {
  return (
    <AppErrorBoundary><Suspense fallback={<PageLoader />}><Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <AppShell />
          </ProtectedRoute>
        }
      >
        <Route index element={<Navigate to="/portfolio" replace />} />
        <Route path="portfolio" element={<PortfolioPage />} />
        <Route path="cities" element={<CitiesPage />} />
        <Route path="cities/:slug" element={<CityPage />} />
        <Route path="roi" element={<RoiPage />} />
        <Route path="scenarios" element={<ScenariosPage />} />
        <Route path="reports" element={<ReportsPage />} />
        <Route path="data-admin" element={<DataAdminPage />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes></Suspense></AppErrorBoundary>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <AppRoutes />
    </AuthProvider>
  );
}
